import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Room, RoomVisibility } from "@/types/rooms";

export function useRooms() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [teamRooms, setTeamRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchRooms = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    setLoading(true);
    const { data } = await supabase
      .from("rooms")
      .select("*")
      .eq("user_id", user.id)
      .is("team_id", null)
      .order("created_at", { ascending: false });

    setRooms((data as Room[]) ?? []);
    setLoading(false);
  };

  const fetchTeamRooms = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get user's team
    const { data: membership } = await supabase
      .from("team_members")
      .select("team_id")
      .eq("user_id", user.id)
      .maybeSingle();

    if (!membership) {
      setTeamRooms([]);
      return;
    }

    const { data } = await supabase
      .from("rooms")
      .select("*")
      .eq("team_id", membership.team_id)
      .order("created_at", { ascending: false });

    setTeamRooms((data as Room[]) ?? []);
  };

  useEffect(() => {
    fetchRooms();
    fetchTeamRooms();
  }, []);

  const createRoom = async (teamId?: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    const insertData: { user_id: string; team_id?: string } = { user_id: user.id };
    if (teamId) {
      insertData.team_id = teamId;
    }

    const { data, error } = await supabase
      .from("rooms")
      .insert([insertData])
      .select()
      .single();

    if (!error && data) {
      if (teamId) {
        setTeamRooms((prev) => [data as Room, ...prev]);
      } else {
        setRooms((prev) => [data as Room, ...prev]);
      }
      return data as Room;
    }
  };

  const renameRoom = async (id: string, name: string) => {
    await supabase.from("rooms").update({ name }).eq("id", id);
    setRooms((prev) => prev.map((r) => (r.id === id ? { ...r, name } : r)));
    setTeamRooms((prev) => prev.map((r) => (r.id === id ? { ...r, name } : r)));
  };

  const deleteRoom = async (id: string) => {
    await supabase.from("rooms").delete().eq("id", id);
    setRooms((prev) => prev.filter((r) => r.id !== id));
    setTeamRooms((prev) => prev.filter((r) => r.id !== id));
  };

  const changeVisibility = async (id: string, visibility: RoomVisibility) => {
    const { data } = await supabase
      .from("rooms")
      .update({ visibility })
      .eq("id", id)
      .select()
      .single();

    if (data) {
      setRooms((prev) => prev.map((r) => (r.id === id ? (data as Room) : r)));
      setTeamRooms((prev) => prev.map((r) => (r.id === id ? (data as Room) : r)));
    }
  };

  return {
    rooms,
    teamRooms,
    loading,
    fetchRooms,
    fetchTeamRooms,
    createRoom,
    renameRoom,
    deleteRoom,
    changeVisibility,
  };
}
